// Helpers for formatting venue certificate metadata in the UI.
// Pure functions, unit-tested in cert_format.test.js.

/**
 * Returns a structured expiry summary for a screen's certificate_not_after
 * field. Caller renders a chip in the matching color.
 *
 * Buckets:
 *   - null / undefined → { label: '—', color: 'default', severity: 'unknown' }
 *   - already past     → { label: 'EXPIRED', color: 'error',   severity: 'expired' }
 *   - <30 days         → { label: 'Nd',       color: 'error',   severity: 'critical' }
 *   - <90 days         → { label: 'Nd',       color: 'warning', severity: 'warning' }
 *   - <2 years         → { label: 'Ny' or 'Nm', color: 'success', severity: 'ok' }
 *   - >=2 years        → { label: 'Ny+',     color: 'success', severity: 'ok' }
 */
export function formatCertExpiry(notAfterIso, now = new Date()) {
  if (!notAfterIso) {
    return { label: '—', color: 'default', severity: 'unknown', daysRemaining: null };
  }
  const notAfter = new Date(notAfterIso);
  if (Number.isNaN(notAfter.getTime())) {
    return { label: '—', color: 'default', severity: 'unknown', daysRemaining: null };
  }
  const ms = notAfter.getTime() - now.getTime();
  const days = Math.floor(ms / 86400000);

  if (days < 0) {
    return { label: 'EXPIRED', color: 'error', severity: 'expired', daysRemaining: days };
  }
  if (days < 30) {
    return { label: `${days}d`, color: 'error', severity: 'critical', daysRemaining: days };
  }
  if (days < 90) {
    return { label: `${days}d`, color: 'warning', severity: 'warning', daysRemaining: days };
  }
  // 2 years = 730 days. Above that, just show "Ny+" since the exact
  // count stops mattering — operator only cares once it's <2y away.
  if (days >= 730) {
    const years = Math.floor(days / 365);
    return { label: `${years}y+`, color: 'success', severity: 'ok', daysRemaining: days };
  }
  if (days >= 365) {
    const years = Math.floor(days / 365);
    return { label: `${years}y`, color: 'success', severity: 'ok', daysRemaining: days };
  }
  // 90–364 days: render in months for readability.
  const months = Math.floor(days / 30);
  return { label: `${months}mo`, color: 'success', severity: 'ok', daysRemaining: days };
}

/** Format an ISO date as a short, human-readable absolute date. */
export function formatCertDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch (_e) {
    return iso;
  }
}

/**
 * Walks the issuer CN to guess "Make · Model" for display.
 * "Barco-ICMP-CA.02" → "Barco ICMP"
 * "Christie-CineLife+-CA.01" → "Christie CineLife+"
 * Returns null when nothing recognizable comes out.
 */
export function describeCertIssuer(issuerCn) {
  if (!issuerCn || typeof issuerCn !== 'string') return null;
  // Strip trailing CA.NN identifier.
  const stripped = issuerCn.replace(/[-_]?CA\.[\w.]+$/i, '');
  const parts = stripped.split(/[-_]/).filter(Boolean);
  if (parts.length === 0) return null;
  return parts.join(' ');
}
